import * as React from 'react';
import { Control, useController } from 'react-hook-form';
import { CmdError } from '@commander-services/gui-components';
import usePrevious from '../../../hooks/usePrevious';

export interface ICheckboxGroupOption {
  item: string;
  value: string;
}

interface ICheckboxInputProps {
  name: string;
  label: string;
  control: Control<any>;
  options: ICheckboxGroupOption[];
  required?: boolean;
  onChangeCallback?: (value: string[]) => void;
}

function CheckboxGroupArrayHookForm(props: ICheckboxInputProps): JSX.Element {
  const prevRequired = usePrevious(props.required, { jsonParse: false });
  const prevOptions = usePrevious(props.options, { jsonParse: false });
  const [isRequired, setIsRequired] = React.useState<boolean>(false);
  const [selectedItems, setSelectedItems] = React.useState<string[]>([]);
  const [options, setOptions] = React.useState<ICheckboxGroupOption[]>([]);

  const {
    field: { ref, onChange, onBlur, value },
    fieldState: { invalid, error },
  } = useController({
    name: props.name,
    control: props.control,
    defaultValue: [], // or the default value
  });

  React.useEffect(() => {
    if (JSON.stringify(prevRequired) !== JSON.stringify(props.required)) {
      setIsRequired(props.required || false);
    }
    if (JSON.stringify(prevOptions) !== JSON.stringify(props.options)) {
      setOptions(props.options || []);
    }
  }, [prevOptions, prevRequired, props.options, props.required]);

  React.useEffect(() => {
    if (value) {
      setSelectedItems(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const dataValue = event.currentTarget.value;

    if (dataValue) {
      if (selectedItems.includes(dataValue)) {
        const newSelectedItems: string[] = [];
        selectedItems.forEach((item: string) => {
          if (item !== dataValue) {
            newSelectedItems.push(item);
          }
        });
        setSelectedItems(newSelectedItems);

        // props.form.setFieldValue(props.field.name, newSelectedItems);
        onChange(newSelectedItems);
      } else {
        const newSelectedItems = selectedItems;
        newSelectedItems.push(dataValue);
        setSelectedItems(newSelectedItems);
        // props.form.setFieldValue(props.field.name, newSelectedItems);
        onChange(newSelectedItems);
      }
    }
  };

  const handleOnChange = (event: React.MouseEvent<HTMLLabelElement, MouseEvent>) => {
    event.preventDefault();
    const dataValue = event.currentTarget.getAttribute('data-value');

    if (dataValue) {
      if (selectedItems.includes(dataValue)) {
        const newSelectedItems: string[] = [];
        selectedItems.forEach((item: string) => {
          if (item !== dataValue) {
            newSelectedItems.push(item);
          }
        });
        setSelectedItems(newSelectedItems);
        if (props.onChangeCallback) {
          props.onChangeCallback(newSelectedItems);
        } else {
          onChange(newSelectedItems);
        }
      } else {
        const newSelectedItems = selectedItems;
        newSelectedItems.push(dataValue);
        setSelectedItems(newSelectedItems);
        if (props.onChangeCallback) {
          props.onChangeCallback(newSelectedItems);
        } else {
          onChange(newSelectedItems);
        }
      }
    }
  };

  return (
    <div className={`checkbox-group-component form-group ${error && 'form-group-error'}`}>
      <div>
        <label className="d-block form-label">
          {isRequired && <span className="text-danger required-field-info">*</span>}
          {props.label}
        </label>
        <div className="d-block ">
          <div>
            <div
              style={{
                display: 'grid',
                gridTemplateRows: 'repeat(2, max-content)',
                gridTemplateColumns: '1fr 1fr 1fr 1fr',
              }}
            >
              {options.length &&
                options.map((option: ICheckboxGroupOption, index: number) => (
                  <div
                    className="e-checkbox"
                    key={`checkbox-${props.name}-${index}`}
                    style={{ marginLeft: 0, marginRight: '10px' }}
                  >
                    <label
                      onClick={handleOnChange}
                      data-cy={`multipleCheckboxes-${option.value}`}
                      data-value={option.value}
                    >
                      <input
                        type="checkbox"
                        value={option.value}
                        checked={selectedItems.includes(String(option.value))}
                        className={selectedItems.includes(String(option.value)) ? 'checked' : ''}
                        onChange={handleOnChangeInput}
                      />
                      <span>{option.item}</span>
                    </label>
                  </div>
                ))}
            </div>
            {error && error.message && <CmdError message={error.message} />}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CheckboxGroupArrayHookForm;
